import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Save, ArrowLeft, Plus, Trash2, AlertCircle, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/Dialog';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { toast } from '@/components/Toaster';

type Priority = 'Low' | 'Medium' | 'High' | 'Urgent';

interface Project { id: string; name: string; project_name: string; }
interface TaskTemplateItem {
  id?: string;
  row_index?: number;
  subject: string;
  description?: string;
  priority: Priority;
  due_offset_days: number;
}
interface TaskTemplate {
  id: string;
  name: string;
  description?: string;
  items: TaskTemplateItem[];
}

interface DraftItem {
  rowId: string;
  subject: string;
  description: string;
  priority: Priority;
  due_offset_days: number;
}

const rid = () => Math.random().toString(36).slice(2);
const todayISO = () => new Date().toISOString().slice(0, 10);

const PARENT_DOCTYPES: { value: string; label: string }[] = [
  { value: '',                 label: '— None —' },
  { value: 'customer',         label: 'Customer' },
  { value: 'supplier',         label: 'Supplier' },
  { value: 'opportunity',      label: 'Opportunity' },
  { value: 'asset',            label: 'Asset' },
  { value: 'purchase_order',   label: 'Purchase Order' },
  { value: 'sales_invoice',    label: 'Sales Invoice' },
  { value: 'purchase_invoice', label: 'Purchase Invoice' },
];

export function TaskTemplateForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['task-template', id],
    enabled: !isNew,
    queryFn: () => api<TaskTemplate>(`/projects/task-templates/${id}`),
  });
  const { data: projectsResp } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api<{ items: Project[] }>('/projects/projects'),
  });
  const projects = projectsResp?.items ?? [];

  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems]             = useState<DraftItem[]>([
    { rowId: rid(), subject: '', description: '', priority: 'Medium', due_offset_days: 0 },
  ]);
  const [err, setErr]                 = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setDescription(existing.description ?? '');
    setItems((existing.items ?? []).map((it) => ({
      rowId: it.id ?? rid(),
      subject: it.subject,
      description: it.description ?? '',
      priority: it.priority,
      due_offset_days: it.due_offset_days ?? 0,
    })));
  }, [existing]);

  function addItem() {
    setItems((s) => [...s, { rowId: rid(), subject: '', description: '', priority: 'Medium', due_offset_days: 0 }]);
  }
  function patchItem(rowId: string, patch: Partial<DraftItem>) {
    setItems((s) => s.map((r) => r.rowId === rowId ? { ...r, ...patch } : r));
  }
  function removeItem(rowId: string) {
    setItems((s) => s.length > 1 ? s.filter((r) => r.rowId !== rowId) : s);
  }

  function validate(): string[] {
    const out: string[] = [];
    if (!name.trim()) out.push('Template name is required.');
    const validItems = items.filter((i) => i.subject.trim());
    if (validItems.length === 0) out.push('Add at least one item.');
    return out;
  }
  function buildBody() {
    return {
      name,
      description: description || undefined,
      items: items
        .filter((i) => i.subject.trim())
        .map((i, idx) => ({
          row_index: idx + 1,
          subject: i.subject,
          description: i.description || undefined,
          priority: i.priority,
          due_offset_days: Number(i.due_offset_days || 0),
        })),
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<TaskTemplate>('/projects/task-templates', { method: 'POST', body: buildBody() }),
    onSuccess: (t) => {
      toast.success('Template created', t.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/task-templates'] });
      navigate({ to: `/projects/task-templates/${t.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Create failed.'),
  });
  // Update endpoint doesn't exist on the backend yet — re-create flow isn't
  // exposed, so once saved a template is immutable in the UI.

  function onCreate() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }

  // ---- Apply-to flow (dialog) -------------------------------------------
  const [applyOpen, setApplyOpen]       = useState(false);
  const [applyProjectID, setApplyProjectID]   = useState<string>('');
  const [applyParentDoctype, setApplyParentDoctype] = useState<string>('');
  const [applyParentID, setApplyParentID] = useState<string>('');
  const [applyStartDate, setApplyStartDate] = useState<string>(todayISO());

  const applyMutation = useMutation({
    mutationFn: () =>
      api<{ task_ids: string[]; count: number }>('/projects/task-templates/apply', {
        method: 'POST',
        body: {
          template_id:    id,
          project_id:     applyProjectID || undefined,
          parent_doctype: applyParentDoctype || undefined,
          parent_id:      applyParentID || undefined,
          start_date:     applyStartDate || undefined,
        },
      }),
    onSuccess: (r) => {
      toast.success(`Created ${r.count} task${r.count === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
      setApplyOpen(false);
      if (applyProjectID) navigate({ to: `/projects/projects/${applyProjectID}` as never });
    },
    onError: (e: { message?: string }) => toast.error('Apply failed', e?.message ?? 'See API logs'),
  });

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.project_name, description: p.name })),
    [projects],
  );

  const title = isNew ? 'New task template' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Operations', to: '/projects' },
          { label: 'Task Templates', to: '/projects/task-templates' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/projects/task-templates' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {isNew && (
              <Button onClick={onCreate} loading={createMutation.isPending}>
                <Save className="size-4" /> Create <Kbd>⌘S</Kbd>
              </Button>
            )}
            {!isNew && (
              <Button onClick={() => setApplyOpen(true)}>
                <Wand2 className="size-4" /> Apply to…
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-4xl space-y-4"
      >
        {err && (
          <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
            <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
            <div className="text-body-sm text-brand-error">{err}</div>
          </Card>
        )}

        <Card>
          <CardTitle>Header</CardTitle>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Name" hint="Required.">
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isNew} placeholder="e.g. Onboard new customer" />
            </Field>
            <Field label="Description" hint="Optional — what this template is for.">
              <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={!isNew} />
            </Field>
          </div>
        </Card>

        <Card padded={false}>
          <div className="flex items-center justify-between p-5 pb-3">
            <div>
              <CardTitle>Items</CardTitle>
              <p className="text-caption text-stone mt-0.5">
                When applied, each item becomes a task. <code className="font-mono text-caption">due_offset_days</code> is added to the apply start date.
              </p>
            </div>
            {isNew && (
              <Button variant="ghost" size="sm" onClick={addItem}><Plus className="size-4" /> Add item</Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                <tr>
                  <th className="text-left font-medium px-3 py-2 w-10">#</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[240px]">Subject</th>
                  <th className="text-left font-medium px-3 py-2 w-32">Priority</th>
                  <th className="text-right font-medium px-3 py-2 w-32">Due offset (days)</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[200px]">Description</th>
                  {isNew && <th className="w-10" />}
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.rowId} className="border-t border-hairline">
                    <td className="px-3 py-2 text-stone">{idx + 1}</td>
                    <td className="px-2 py-1.5">
                      {isNew
                        ? <Input value={it.subject} onChange={(e) => patchItem(it.rowId, { subject: e.target.value })} />
                        : <span className="px-3 text-charcoal">{it.subject}</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {isNew
                        ? <select className="input-base" value={it.priority} onChange={(e) => patchItem(it.rowId, { priority: e.target.value as Priority })}>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Urgent">Urgent</option>
                          </select>
                        : <span className="px-3 text-charcoal">{it.priority}</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {isNew
                        ? <NumericInput value={String(it.due_offset_days)} onChange={(v) => patchItem(it.rowId, { due_offset_days: Number(v || 0) })} decimalPlaces={0} />
                        : <span className="num text-right block px-3">{it.due_offset_days}</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {isNew
                        ? <Input value={it.description} onChange={(e) => patchItem(it.rowId, { description: e.target.value })} />
                        : <span className="px-3 text-charcoal">{it.description || '—'}</span>}
                    </td>
                    {isNew && (
                      <td className="px-2 py-1.5">
                        <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => removeItem(it.rowId)} disabled={items.length <= 1}>
                          <Trash2 className="size-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Apply dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>Apply template</DialogTitle>
          <DialogDescription>Pick the target. Every item turns into a task; due dates are start + offset days.</DialogDescription>
          <div className="space-y-3 mt-3">
          <Field label="Start date" hint="Used as the base for each item's due-offset.">
            <Input type="date" value={applyStartDate} onChange={(e) => setApplyStartDate(e.target.value)} />
          </Field>
          <Field label="Project" hint="Either pick a project, or a parent doctype + id below.">
            <Combobox
              options={projectOptions}
              value={applyProjectID || null}
              onChange={(v) => setApplyProjectID(v ?? '')}
              placeholder="— None —" clearable
            />
          </Field>
          <Field label="Parent doctype">
            <select className="input-base" value={applyParentDoctype} onChange={(e) => setApplyParentDoctype(e.target.value)}>
              {PARENT_DOCTYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          {applyParentDoctype && (
            <Field label="Parent ID">
              <Input value={applyParentID} onChange={(e) => setApplyParentID(e.target.value)} placeholder="e.g. cust_01..." className="font-mono text-caption" />
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button onClick={() => applyMutation.mutate()} loading={applyMutation.isPending}>
              <Wand2 className="size-4" /> Apply
            </Button>
          </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
